import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { fetchAtom } from "@/lib/api";
import KanjiGlyph from "@/components/KanjiGlyph";
import { TEST_IDS } from "@/constants/testIds";

const JLPT_COLOR = {
  N5: "#4A6D7C",
  N4: "#6E7A5A",
  N3: "#D29C4C",
  N2: "#B86C45",
  N1: "#8B4546",
};

// Palette used to colour-code depth-1 sub-parts inside the parent kanji.
// Hand-tuned for readability against the washi background.
const PART_PALETTE = [
  "#C84B31", // shu (vermillion)
  "#4A6D7C", // indigo
  "#6E7A5A", // moss
  "#D29C4C", // amber
  "#B86C45", // burnt orange
  "#8B4546", // deep red
  "#2A4B6A", // navy
];

// Flatten + collect a parent-keyed set of phonetic children glyphs so we can
// highlight the right ones in nested rows.
function flattenTree(root) {
  const rows = [];
  const phoneticByParentRow = []; // index by depth -> Set of chars that are phonetic at that depth
  const walk = (node, depth) => {
    if (!rows[depth]) rows[depth] = [];
    if (!phoneticByParentRow[depth + 1]) phoneticByParentRow[depth + 1] = new Set();
    rows[depth].push(node);
    (node.phonetic_in || []).forEach((p) => phoneticByParentRow[depth + 1].add(p.component));
    (node.children || []).forEach((c) => walk(c, depth + 1));
  };
  walk(root, 0);
  return { rows, phoneticByParentRow };
}

function PartButton({ node, isRoot, onSelect, delay = 0, phoneticChars, onHover, partColor }) {
  const acc = node.is_kanji && node.jlpt ? JLPT_COLOR[node.jlpt] : "#2A4B6A";
  const isPhonetic = phoneticChars?.has(node.char);
  // A part is clickable if it's a kanji itself (青/寺/工 …) OR a
  // positional radical variant that maps to a parent kanji (氵→水,
  // 扌→手, 忄→心 …). The backend's part_label sets `parent_kanji` for
  // both cases. Ghost radicals without a parent stay non-clickable.
  const targetChar = node.parent_kanji
    || (node.is_kanji ? node.char : null);
  const isClickable = !!targetChar && !isRoot;
  // Priority: explicit partColor > phonetic shu > JLPT acc > root shu
  const borderColor = isRoot
    ? "#C84B31"
    : (partColor || (isPhonetic ? "#C84B31" : acc));
  const labelText = node.is_kanji
    ? node.meaning
    : (node.meaning ? (node.meaning.toLowerCase().includes("radical") ? node.meaning : `${node.meaning} radical`) : `${node.char} radical`);
  // Tooltip flags variant→parent navigation so the user understands
  // why clicking 氵 lands on 水's card and not on a 氵-only page.
  const variantHint = !node.is_kanji && node.parent_kanji
    ? ` · opens ${node.parent_kanji}'s card (parent kanji)`
    : "";
  return (
    <motion.button
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
      data-testid={TEST_IDS.focusTreeNode(node.char)}
      data-demo-part={node.char}
      onClick={() => isClickable && onSelect && onSelect(targetChar)}
      onMouseEnter={() => !isRoot && onHover?.(node.char)}
      onMouseLeave={() => !isRoot && onHover?.(null)}
      onFocus={() => !isRoot && onHover?.(node.char)}
      onBlur={() => !isRoot && onHover?.(null)}
      onTouchStart={() => !isRoot && onHover?.(node.char)}
      className={`flex items-center gap-2 px-3 py-2 bg-washi transition-colors border ${
        isRoot ? "border-2"
          : isClickable ? "hover:border-shu cursor-pointer"
          : "cursor-default"
      }`}
      style={{
        borderColor,
        borderWidth: (isRoot || isPhonetic || partColor) ? 2 : 1,
        background: isPhonetic ? "rgba(200,75,49,0.06)" : "#F4F1EB",
      }}
      title={(isPhonetic ? `phonetic clue · ${labelText}` : labelText) + variantHint}
    >
      <span
        className={`font-serif leading-none ${isRoot ? "text-5xl" : "text-3xl"}`}
        style={
          !node.is_kanji
            ? { color: partColor || acc }
            : { color: partColor || (isPhonetic ? "#C84B31" : "#1E1B18") }
        }
      >
        {node.char}
      </span>
      <div className="flex flex-col items-start">
        <span className="text-[10px] uppercase tracking-widest text-sumiSoft font-sans max-w-[120px] truncate">
          {labelText}
        </span>
        {isPhonetic && (
          <span className="text-[9px] uppercase tracking-widest text-shu font-sans mt-0.5">
            phonetic clue
          </span>
        )}
      </div>
    </motion.button>
  );
}

export default function BuildTree({ ch, onSelect, compoundsAbove = null }) {
  const [hoveredPart, setHoveredPart] = useState(null);
  // For high-fanout kanji (water has 115 used_in entries after the
  // variant-merge in iter-39) we cap the initial render at 60 and offer
  // a "show all" toggle. Keeps the page lightweight while still letting
  // users explore the whole family when they want to.
  const [showAllUsedIn, setShowAllUsedIn] = useState(false);
  const q = useQuery({
    queryKey: ["atom", ch],
    queryFn: () => fetchAtom(ch, 4),
    enabled: !!ch,
  });

  // Depth-1 colour palette (must be computed unconditionally to satisfy
  // react-hooks/rules-of-hooks — degrades to {} when data isn't ready).
  const rootChildren = q.data?.root?.children || [];
  const partColors = useMemo(() => {
    const map = {};
    rootChildren.forEach((n, i) => {
      if (!(n.char in map)) {
        map[n.char] = PART_PALETTE[i % PART_PALETTE.length];
      }
    });
    return map;
  }, [q.data?.root?.children]);
  const glyphParts = useMemo(
    () => Object.entries(partColors).map(([char, color]) => ({ char, color })),
    [partColors],
  );

  if (!ch) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center text-sumiSoft">
        <p className="font-sans text-sm">Pick a kanji to see how it builds up.</p>
      </div>
    );
  }
  if (q.isLoading || !q.data) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sumiSoft animate-pulse">
          building…
        </p>
      </div>
    );
  }

  const usedIn = q.data.used_in || [];
  const root = q.data.root;
  const { rows, phoneticByParentRow } = flattenTree(root);
  const isAtomic = !root.children || root.children.length === 0;

  return (
    <div data-testid={TEST_IDS.focusTreeContainer} className="p-4 md:p-6 overflow-y-auto h-full">
      {/* Glyph: color-coded by depth-1 parts; hover highlights one part */}
      <div className="flex flex-col items-center gap-3 mb-4">
        <div className="border border-shu/40 p-2 bg-washi" data-testid="build-glyph-frame">
          <KanjiGlyph
            ch={root.char}
            parts={glyphParts}
            highlight={hoveredPart}
            size={170}
          />
        </div>
        <div className="flex flex-col items-center">
          <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-sumiSoft">
            {root.meaning}
          </span>
          {isAtomic && (
            <span
              data-testid="build-atomic-label"
              className="font-sans text-[9px] uppercase tracking-[0.25em] text-sumiSoft mt-1 italic"
            >
              primitive — no further parts
            </span>
          )}
          {!isAtomic && (
            <span className="font-sans text-[9px] uppercase tracking-[0.25em] text-sumiSoft mt-1">
              colours match the parts below · hover a part to focus
            </span>
          )}
        </div>
      </div>

      {/* depth rows BELOW the glyph */}
      <div data-testid="build-depth-rows" className="flex flex-col items-center gap-4 mb-6">
        {rows.slice(1).map((row, idx) => {
          const depth = idx + 1;
          return (
            <div key={depth} className="flex flex-wrap items-center justify-center gap-3">
              {row.map((n, i) => (
                <PartButton
                  key={n.char + "-" + depth + "-" + i}
                  node={n}
                  isRoot={false}
                  onSelect={onSelect}
                  delay={depth * 0.06 + i * 0.02}
                  phoneticChars={phoneticByParentRow[depth]}
                  onHover={depth === 1 ? setHoveredPart : undefined}
                  partColor={depth === 1 ? partColors[n.char] : undefined}
                />
              ))}
            </div>
          );
        })}
        {rootChildren.length === 0 && (
          <p className="font-sans text-xs text-sumiSoft text-center italic max-w-sm">
            This kanji is itself a primary radical (no smaller meaningful parts in the standard decomposition).
          </p>
        )}
      </div>

      {compoundsAbove && (
        <div className="mb-6">
          {compoundsAbove}
        </div>
      )}

      {usedIn.length > 0 && (
        <div className="mb-2" data-testid="focus-grows-into">
          <p className="text-[10px] uppercase tracking-[0.3em] text-sumiSoft font-sans mb-2">
            grows into ({usedIn.length})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {(showAllUsedIn ? usedIn : usedIn.slice(0, 60)).map((u) => (
              <button
                key={u.char}
                onClick={() => onSelect && onSelect(u.char)}
                title={u.meaning}
                data-testid={TEST_IDS.focusUsedInChip(u.char)}
                className="font-serif text-2xl text-sumi hover:text-shu border border-transparent hover:border-shu px-1.5 py-0.5"
              >
                {u.char}
              </button>
            ))}
            {!showAllUsedIn && usedIn.length > 60 && (
              <button
                type="button"
                onClick={() => setShowAllUsedIn(true)}
                data-testid="grows-into-show-all"
                className="font-sans text-[10px] uppercase tracking-[0.25em] text-shu border border-shu px-2 py-1 hover:bg-shu hover:text-washi transition-colors"
              >
                show all {usedIn.length} →
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
